import { useEffect } from 'react'
import { AppState, Platform } from 'react-native'
import * as Device from 'expo-device'
import * as Notifications from 'expo-notifications'
import { atom, useSetAtom } from 'jotai'
import {
  applyForegroundReading,
  osBannerFromPermission,
  type OsSupportBanner,
} from './osSupportBannerRule'

export { applyTokenVerdict } from './osSupportBannerRule'

/**
 * ATL-1345 — the one seam between the support toast and the native notification stack. Every
 * `react-native` / `expo-*` call the toast depends on lives here, so tests mock this file rather
 * than the native modules (see the rule and its reasoning in `osSupportBannerRule.ts`).
 */

/**
 * Whether the OS will show its own banner for a support push right now.
 *
 * Web starts — and stays — at `not-shown`: there is no push on web, so the toast is always the
 * announcement there and the gate is effectively ignored. Native starts at `unknown` until the
 * push prompt reports, and the toast waits rather than guessing: declining while unknown would
 * advance its marker and swallow the message for good.
 */
export const osSupportBannerAtom = atom<OsSupportBanner>(
  Platform.OS === 'web' ? 'not-shown' : 'unknown'
)

/**
 * The permission-only verdict the push prompt publishes the moment the permission step is done —
 * BEFORE it asks for a token, so a slow or hung token request cannot leave the toast waiting on
 * `unknown`. Worst case this is exactly the PM's rule (suppress when push is granted); the token
 * outcome, reported afterwards, can only relax it.
 */
export const bannerAfterPermissionStep = (
  perms: Notifications.NotificationPermissionsStatus
): Exclude<OsSupportBanner, 'unknown'> =>
  osBannerFromPermission(perms, { isPhysicalDevice: Device.isDevice === true })

/** The final verdict, once the push prompt knows whether it obtained a token. */
export const bannerAfterTokenStep = (
  perms: Notifications.NotificationPermissionsStatus,
  hasPushToken: boolean
): Exclude<OsSupportBanner, 'unknown'> =>
  osBannerFromPermission(perms, { isPhysicalDevice: Device.isDevice === true, hasPushToken })

/**
 * Re-read the permission. Never rejects: an unreadable permission is not evidence that a banner
 * will appear, so it reads as `not-shown` and the toast takes over — a duplicate is recoverable,
 * a missing announcement is not.
 */
export const readPermissionBanner = async (): Promise<Exclude<OsSupportBanner, 'unknown'>> => {
  try {
    return bannerAfterPermissionStep(await Notifications.getPermissionsAsync())
  } catch {
    return 'not-shown'
  }
}

/**
 * Keep the verdict honest while the app runs. A merchant can switch notifications off in Settings
 * and come straight back; the banner is gone at that moment, so the toast must take over without
 * waiting for a restart. Only downgrades — see `applyForegroundReading`. No-op on web.
 */
export const useOsSupportBannerRefresh = (): void => {
  const setBanner = useSetAtom(osSupportBannerAtom)
  useEffect(() => {
    if (Platform.OS === 'web') return undefined
    let alive = true
    let latestRead = 0
    const refresh = (): void => {
      const mine = ++latestRead
      void readPermissionBanner().then((reading) => {
        // Only the newest read may apply: two foregrounds in quick succession can resolve out of
        // order, and an older reading must not overwrite a newer one.
        if (!alive || mine !== latestRead) return
        setBanner((current) => applyForegroundReading(current, reading))
      })
    }
    const subscription = AppState.addEventListener('change', (next) => {
      if (next === 'active') refresh()
    })
    return () => {
      alive = false
      subscription.remove()
    }
  }, [setBanner])
}
